<script lang="ts" setup>
import {
  DrawerContent,
  DrawerOverlay,
  DrawerTitle,
  DrawerPortal,
  DrawerRoot,
  DrawerTrigger,
} from 'vaul-vue'

type Specie = {
  title: string
  latinName: string
  family: string
  text: string
  portableTextSimple: string
  color: string
  status: string
  media: Record<string, unknown>
}

const _props = defineProps<{ data?: Specie }>()
const isOpen = ref(false)
const handleUpdate = (open: boolean) => {
  if (!open) {
    isOpen.value = false
  }
}

const locale = useI18n().locale
const closeLabel = computed(() => {
  return locale.value === 'da' ? 'Luk' : 'Close'
})
</script>

<template>
  <div class="relative h-full">
    <DrawerRoot
      v-if="data && (data.text || data.portableTextSimple)"
      :open="isOpen"
      should-scale-background
      class="relative h-full"
      @on-open-change="isOpen = true"
      @update:open="handleUpdate"
    >
      <DrawerTrigger
        class="relative flex aspect-[6/7] h-full w-full cursor-pointer items-center justify-between gap-4 text-left text-sm"
        @click="isOpen = true"
      >
        <div
          class="content p-bleed relative z-[2] mt-auto flex w-full flex-col justify-between gap-10 text-white"
        >
          <div class="text-content flex h-full w-full flex-col gap-10">
            <div class="text-content-heading grid gap-5">
              <span class="w-full font-sans text-xs italic opacity-50">{{ data.latinName }}</span>
              <h3 class="font-display w-full text-xl">{{ data.title }}</h3>
            </div>
            <div class="flex w-full flex-col gap-10">
              <ElementsArrowButton v-if="data.text"
                ><span class="font-display text-base">{{
                  $t('label.readmore')
                }}</span></ElementsArrowButton
              >
            </div>
          </div>
        </div>
        <div
          class="overlay absolute top-0 left-0 z-[1] block h-full w-full overflow-clip rounded-sm bg-black opacity-20"
        />
        <div
          class="media-wrapper !absolute top-0 left-0 z-[0] h-full w-full overflow-clip rounded-sm"
        >
          <CommonMedia :media="data.media" class="h-full" :aspect-mobile="4 / 3" />
        </div>
      </DrawerTrigger>

      <DrawerPortal>
        <DrawerOverlay
          class="fixed inset-0 z-900 bg-black/20 backdrop-blur-[2px]"
          @click="isOpen = false"
        />

        <DrawerContent
          class="fixed right-2 bottom-0 left-2 z-1000 h-fit rounded-t-sm bg-(--theme-bg) text-(--theme-fg) outline-none"
        >
          <DrawerTitle class="flex justify-center p-4">
            <button
              :aria-label="closeLabel"
              class="ml-auto inline-flex cursor-pointer items-center gap-2.5 text-(--theme-fg)"
              @click="isOpen = false"
            >
              <span class="font-display text-sm">{{ closeLabel }}</span>
              <svg
                width="11"
                height="11"
                viewBox="0 0 11 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line x1="9.51176" y1="1.35355" x2="1.35283" y2="9.51248" stroke="currentColor" />
                <line x1="9.34762" y1="9.51273" x2="1.18869" y2="1.35381" stroke="currentColor" />
              </svg>
            </button>
          </DrawerTitle>

          <div
            class="px-bleed drawer-content grid max-h-[calc(100dvh-4rem)] gap-10 overflow-auto overscroll-contain py-10"
            data-vaul-no-drag
          >
            <div class="text-content flex w-full flex-col gap-10">
              <div class="text-content-heading grid gap-5">
                <span class="w-full font-sans text-xs italic opacity-50">{{ data.latinName }}</span>
                <h3 class="font-display w-full text-lg">{{ data.title }}</h3>
              </div>
              <div class="flex w-full flex-col gap-10">
                <ElementsPortableTextWrapper
                  v-if="data.portableTextSimple"
                  :content="data.portableTextSimple"
                />
                <p v-else-if="data.text" class="text-base">{{ data.text }}</p>
              </div>
            </div>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </DrawerRoot>
  </div>
</template>
